import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';

interface MasterSystemState {
  /**
   * Organization the MASTER user is currently scoping the master panel to.
   * `null` means "platform default" — no `x-selected-organization-id` header is
   * sent and the backend falls back to the default org's database.
   */
  selectedOrganizationId: string | null;

  // Actions
  setSelectedOrganizationId: (orgId: string | null) => void;
}

/**
 * Master System Store (Zustand)
 *
 * Holds the MASTER org selector state. A MASTER user picks an organization in
 * the master-panel header; the API interceptor reads this value (via a
 * callback registered in the auth store) and attaches
 * `x-selected-organization-id` to every `/system/*` request so the backend
 * scopes those views to the chosen org's database.
 *
 * ORG_OWNER users never set this — they are locked to their own organization.
 */
export const useMasterSystemStore = create<MasterSystemState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        selectedOrganizationId: null,

        // Actions
        setSelectedOrganizationId: (orgId) =>
          set({ selectedOrganizationId: orgId }),
      }),
      {
        name: 'master-system-store',
        partialize: (state) => ({
          selectedOrganizationId: state.selectedOrganizationId,
        }),
      },
    ),
    { name: 'master-system-store' },
  ),
);
